<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps<{ src: string; poster?: string; alt: string; sound?: boolean }>()

const el = ref<HTMLVideoElement | null>(null)
// Silent clips autoplay only while half in view; with reduced motion they just
// show controls. `window` and IntersectionObserver are touched in onMounted only,
// so prerender never reaches them.
const controls = ref(!!props.sound)
let observer: IntersectionObserver | undefined

onMounted(() => {
  const video = el.value
  if (!video || props.sound) return
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    controls.value = true
    return
  }
  if (typeof IntersectionObserver === 'undefined') {
    controls.value = true
    return
  }
  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) video.play().catch(() => { controls.value = true })
        else video.pause()
      }
    },
    { threshold: 0.5 },
  )
  observer.observe(video)
})
onBeforeUnmount(() => observer?.disconnect())
</script>

<template>
  <video
    ref="el"
    :src="src"
    :poster="poster"
    :aria-label="alt"
    :controls="controls"
    :muted="!sound"
    :loop="!sound"
    preload="none"
    playsinline
    class="block w-full h-auto aspect-video bg-black object-contain"
  ></video>
</template>
